import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, PlugZap, CheckCircle2, XCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { usePublicLang } from "../lib/publicLang";
import PlatformsGuideEn from "./PlatformsGuideEn";

const PLATFORMS = [
  {
    name: "MetaTrader 4",
    tagline: "Le standard historique du forex",
    strengths: ["Écosystème d'EA et d'indicateurs le plus vaste (3000+ brokers)", "VPS 24/7 et automatisation éprouvée"],
    gaps: ["Aucune validation statistique (PBO, DSR, permutation)", "Interface datée, aucun support éditeur", "MQL4 inaccessible aux non-développeurs"],
    export: ["Terminal (Ctrl+T) → Historique du compte", "Clic droit → « Tout l'historique »", "Clic droit → « Rapport » (HTML) ou export CSV — les deux formats sont acceptés par ANATRADEX"],
  },
  {
    name: "MetaTrader 5",
    tagline: "Le successeur multi-actifs",
    strengths: ["Multi-actifs (actions, indices, crypto)", "Backtests multi-threads plus riches"],
    gaps: ["Toujours zéro analyse de robustesse statistique", "Backtests lourds en mémoire côté client", "Fragmentation avec l'écosystème MT4"],
    export: ["Boîte à outils → Historique", "Clic droit → « Rapport » → HTML ou export", "Importez le fichier tel quel dans ANATRADEX"],
  },
  {
    name: "cTrader",
    tagline: "L'exécution ECN transparente",
    strengths: ["Exécution ECN/STP transparente", "C# moderne, plus robuste que MQL"],
    gaps: ["Automatisation réservée aux développeurs C#", "Pas de communauté intégrée", "Aucun test de significativité statistique"],
    export: ["Onglet « History » en bas de la plateforme", "Clic droit → « Export to CSV »", "Format CSV natif, directement compatible ANATRADEX"],
  },
  {
    name: "TradingView",
    tagline: "Le meilleur charting du marché",
    strengths: ["Charting inégalé + Pine Script accessible", "Cloud, communauté d'idées massive"],
    gaps: ["Pas d'exécution native — l'historique réel est chez votre broker", "Freemium limitant (alertes, multi-charts payants)", "Pine Script limité pour la validation statistique sérieuse"],
    export: ["L'historique de trades vit chez votre broker connecté (OANDA, IBKR…)", "Exportez le CSV depuis le portail de votre broker", "ANATRADEX mappe automatiquement les colonnes standard"],
  },
];

export default function PlatformsGuide() {
  const [lang, setLang] = usePublicLang();
  usePageMeta({
    title: "ANATRADEX + MT4 / MT5 / cTrader / TradingView — la couche d'analyse statistique",
    description: "ANATRADEX ne remplace pas votre plateforme de trading : il valide statistiquement vos stratégies (PBO, Deflated Sharpe, tests de permutation). Guide d'export MT4, MT5, cTrader, TradingView.",
  });
  if (lang === "en") return <PlatformsGuideEn lang={lang} setLang={setLang} />;
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="platforms-page">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Plateformes</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Retour
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-4">
          <PlugZap size={12} /> Compatible avec votre plateforme
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-4">
          Gardez votre plateforme.<br />Ajoutez la validation statistique.
        </h1>
        <p className="text-white/60 text-lg mb-4 max-w-3xl leading-relaxed">
          MT4, MT5, cTrader et TradingView sont d&apos;excellents outils de charting et d&apos;exécution.
          Mais <b className="text-white">aucun ne répond à la seule question qui compte</b> : votre stratégie
          a-t-elle un vrai edge, ou avez-vous eu de la chance ? ANATRADEX est la couche d&apos;analyse
          qui vient <em>après</em> : importez votre historique, obtenez PBO, Deflated Sharpe et tests de
          permutation — reproductibles au chiffre près.
        </p>
        <div className="text-xs text-white/40 mb-12">
          Zéro code requis · Import CSV/HTML en 2 minutes · <Link to="/methodology" className="text-accent-blue underline">Formules publiques</Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={`platform-card-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
              <h2 className="font-heading text-xl font-bold">{p.name}</h2>
              <div className="text-xs text-white/50 mb-4">{p.tagline}</div>
              <div className="space-y-1.5 mb-3">
                {p.strengths.map((s) => (
                  <div key={s} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle2 size={14} className="text-up shrink-0 mt-0.5" /> {s}
                  </div>
                ))}
              </div>
              <div className="space-y-1.5 mb-4">
                {p.gaps.map((g) => (
                  <div key={g} className="flex items-start gap-2 text-sm text-white/50">
                    <XCircle size={14} className="text-down shrink-0 mt-0.5" /> {g}
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-black/30 border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-accent-blue mb-2">Exporter votre historique</div>
                <ol className="space-y-1 text-xs text-white/60 list-decimal pl-4">
                  {p.export.map((e) => <li key={e}>{e}</li>)}
                </ol>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-up/30 bg-up/5 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold mb-3">Testez sur vos propres trades — gratuitement</h2>
          <p className="text-white/60 text-sm max-w-2xl mx-auto mb-6">
            Le tier Free inclut <b className="text-white">1 Rapport d&apos;Analyse Statistique par mois sur vos
            trades importés</b>. Pas de démo à croire sur parole : vos données, vos chiffres, notre méthodologie
            ouverte. Chaque rapport affiche son seed et ses données brutes sont exportables.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/app/import" className="btn btn-primary text-base px-8 py-4" data-testid="platforms-import-cta">
              Importer mes trades <ArrowRight size={14} />
            </Link>
            <Link to="/methodology" className="btn text-base px-8 py-4" data-testid="platforms-methodology-cta">
              Lire la méthodologie
            </Link>
          </div>
        </div>

        <p className="mt-8 text-[11px] text-white/35 leading-relaxed" data-testid="platforms-risk-disclaimer">
          ⚠️ ANATRADEX est un outil d&apos;analyse statistique, pas un conseil en investissement. ANATRADEX n&apos;est pas
          enregistré comme Conseiller en Investissements Financiers (CIF) auprès de l&apos;AMF/ORIAS. Le trading comporte
          un risque de perte pouvant atteindre 100 % du capital — 70 à 85 % des traders particuliers perdent de
          l&apos;argent (ESMA).
        </p>
      </div>
    </div>
  );
}
